import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderOpen, MessageSquare, FileText, Receipt, Plus, ShieldCheck, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatCard, Card, LoadingBlock, EmptyState, formatDate } from "@/components/Common";
import StatusBadge from "@/components/StatusBadge";
import { BalanceCard, MarketCards, MarketChart, MarketSnapshot } from "@/components/finance/MarketWidgets";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { walletName } from "@/data/site";
import api from "@/lib/api";

export default function Overview() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? it) : it);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/portal/overview").then(({ data }) => setData(data)).catch(() => setData(false)); }, []);
  if (data === null) return <LoadingBlock />;
  if (data === false) return <EmptyState title={L("Errore nel caricamento", "Loading error", "Ladefehler")} />;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading text-2xl font-medium text-white">{L("Benvenuto", "Welcome", "Willkommen")}, {user?.first_name} 👋</h2>
          <p className="mt-1">
            {data.email_verified
              ? <span className="inline-flex items-center gap-1.5 text-emerald-400 text-sm"><ShieldCheck className="h-4 w-4" />{L("Account verificato", "Account verified", "Konto verifiziert")}</span>
              : <span className="inline-flex items-center gap-1.5 text-gold text-sm"><ShieldAlert className="h-4 w-4" />{L("Email da verificare", "Email to verify", "E-Mail zu bestätigen")}</span>}
          </p>
        </div>
        <Button asChild className="bg-gold text-navy hover:bg-gold-hover" data-testid="new-case-btn"><Link to="/avvia-recupero"><Plus className="h-4 w-4 mr-1.5" />{L("Apri una nuova pratica", "Open a new case", "Neuen Fall eröffnen")}</Link></Button>
      </div>

      <BalanceCard />

      <Card className="p-5 border-gold/20 bg-gradient-to-br from-gold/5 to-transparent" data-testid="eurla-card-dashboard">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-base text-white">{L("Il tuo caso richiede assistenza legale?", "Does your case need legal assistance?", "Benötigt Ihr Fall rechtliche Unterstützung?")}</h3>
            <p className="mt-1 text-sm text-slate-400">{L("Consulta i professionisti e le risorse legali presentate sul sito EURLA.", "Explore the legal professionals and resources presented on the EURLA website.", "Entdecken Sie die Rechtsexperten und Ressourcen auf der EURLA-Website.")}</p>
          </div>
          <a href="https://eurla.eu/" target="_blank" rel="noopener noreferrer" data-testid="eurla-link-dashboard" className="shrink-0 inline-flex items-center justify-center gap-2 rounded-lg border border-gold/40 px-5 h-10 text-sm font-medium text-gold hover:bg-gold/10 transition-colors">
            {L("Visita EURLA", "Visit EURLA", "EURLA besuchen")}
          </a>
        </div>
      </Card>

      <MarketCards />
      <MarketChart />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Case summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={FolderOpen} label={L("Pratiche attive", "Active cases", "Aktive Fälle")} value={data.active_cases} tone="cyan" testid="stat-active-cases" />
            <StatCard icon={MessageSquare} label={L("Messaggi non letti", "Unread messages", "Ungelesene Nachrichten")} value={data.unread_messages} tone="gold" testid="stat-unread" />
            <StatCard icon={FileText} label={L("Documenti richiesti", "Requested docs", "Angeforderte Dokumente")} value={data.document_requests} tone="slate" testid="stat-doc-requests" />
            <StatCard icon={Receipt} label={L("Fatture aperte", "Open invoices", "Offene Rechnungen")} value={data.open_invoices} tone="slate" testid="stat-open-invoices" />
          </div>
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg text-white">{L("Pratiche di recupero", "Recovery cases", "Wiederherstellungsfälle")}</h3>
              <Link to="/portal/cases" className="text-sm text-cyanx hover:text-white">{L("Vedi tutte", "View all", "Alle ansehen")} →</Link>
            </div>
            {data.recent_cases.length === 0 ? (
              <EmptyState icon={FolderOpen} title={L("Nessuna pratica", "No cases", "Keine Fälle")}
                action={<Button asChild className="bg-gold text-navy"><Link to="/avvia-recupero">{L("Apri una pratica", "Open a case", "Fall eröffnen")}</Link></Button>} />
            ) : (
              <div className="space-y-2">
                {data.recent_cases.map((c) => (
                  <Link key={c.id} to={`/portal/cases/${c.id}`} className="flex items-center justify-between rounded-lg border border-white/10 bg-navy px-4 py-3 hover:border-cyanx/30" data-testid={`recent-case-${c.reference}`}>
                    <div><p className="text-sm text-white font-mono">{c.reference}</p><p className="text-xs text-slate-500">{walletName(c.wallet_type)} · {formatDate(c.created_at)}</p></div>
                    <StatusBadge status={c.status} />
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </div>
        <MarketSnapshot />
      </div>
    </div>
  );
}
